/**
 * Router-level allowlist enforcement for restricted staff — mirrors
 * src/modules/location-session/components/LocationRouteEnforcer.tsx exactly
 * (see that file's header for the full rationale). Covers every route KIND,
 * not just the `kind:"page"` entries RouteGateGuard wraps: a
 * `kind:"redirect"` entry or the "*" NotFound catch-all bypasses
 * RouteGateGuard entirely, so a restricted account landing on one of those
 * needs this independent, route-kind-agnostic check. Operates on the live
 * `location.pathname` via isRestrictedAllowedPathname (matchPath, handles
 * no dynamic segments today but keeps parity with the location version's
 * shape). Defense in depth, not a replacement — RouteGateGuard's per-page
 * check still runs first and prevents a gated page's own content from
 * flashing before this outer check would redirect.
 */
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { isRestrictedAllowedPathname, RESTRICTED_STAFF_HOME_PATH } from './restrictedAllowlist';
import { useRestrictedStaff } from '@/hooks/useRestrictedStaff';

export function RestrictedRouteEnforcer({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isRestricted, isLoading } = useRestrictedStaff();

  if (!isLoading && isRestricted && !isRestrictedAllowedPathname(location.pathname)) {
    return <Navigate to={RESTRICTED_STAFF_HOME_PATH} replace />;
  }

  return <>{children}</>;
}
